import * as Yup from 'yup';
import { get } from 'lodash';

import { Nullable } from '~globals/types';
import {
  AdminBillingListBillingBase,
  AdminBillingListBillingUpadateBody,
  AdminBillingListBillingZipCodeRange,
  AdminBillingListBillingCapacityRange,
  AdminBillingListBillingPriceRange,
  AdminBillingListBillingServiceTypes,
} from '~services/admin/billingList/billing/types';
import {
  parseSkillsStringToArray,
  parseSkillListToString,
} from '~utils/vehicle';
import { VALIDATIONS_ERROR } from '~constants/validationsError';

export const FIELDS_NAME = {
  CODE: 'code',
  RATE: 'rate',
  ENABLE_RANGE_ZIP_CODES: 'enableRangeZipCodes',
  ZIP_CODES: 'zipCodes',
  ENABLE_RANGE_UNIT_1: 'enableRangeUnit1',
  UNIT_1_MIN: 'unit1Min',
  UNIT_1_MAX: 'unit1Max',
  ENABLE_RANGE_UNIT_2: 'enableRangeUnit2',
  UNIT_2_MIN: 'unit2Min',
  UNIT_2_MAX: 'unit2Max',
  ENABLE_RANGE_PRICE: 'enableRangePrice',
  PRICE_MIN: 'priceMin',
  PRICE_MAX: 'priceMax',
  ENABLE_RANGE_SERVICES: 'enableRangeServices',
  SERVICES_TYPES: 'servicesTypes',
} as const;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getInitialValues = (
  data: Nullable<AdminBillingListBillingBase>,
) => ({
  [FIELDS_NAME.CODE]: data?.billingCode ?? '',
  [FIELDS_NAME.RATE]: String(data?.shipPrice ?? ''),
  [FIELDS_NAME.ENABLE_RANGE_ZIP_CODES]: Boolean(
    data?.billZipCodeRange ?? false,
  ),
  [FIELDS_NAME.ZIP_CODES]: data?.billZipCodeRange?.zipcodeRange ?? '',
  [FIELDS_NAME.ENABLE_RANGE_UNIT_1]: Boolean(
    data?.billingUnit1CapacityRange ?? false,
  ),
  [FIELDS_NAME.UNIT_1_MIN]: String(
    data?.billingUnit1CapacityRange?.unitMinimumCapacity ?? '',
  ),
  [FIELDS_NAME.UNIT_1_MAX]: String(
    data?.billingUnit1CapacityRange?.unitMaximumCapacity ?? '',
  ),
  [FIELDS_NAME.ENABLE_RANGE_UNIT_2]: Boolean(
    data?.billingUnit2CapacityRange ?? false,
  ),
  [FIELDS_NAME.UNIT_2_MIN]: String(
    data?.billingUnit2CapacityRange?.unitMinimumCapacity ?? '',
  ),
  [FIELDS_NAME.UNIT_2_MAX]: String(
    data?.billingUnit2CapacityRange?.unitMaximumCapacity ?? '',
  ),
  [FIELDS_NAME.ENABLE_RANGE_PRICE]: Boolean(data?.billingPriceRange ?? false),
  [FIELDS_NAME.PRICE_MIN]: String(data?.billingPriceRange?.minimumPrice ?? ''),
  [FIELDS_NAME.PRICE_MAX]: String(data?.billingPriceRange?.maximumPrice ?? ''),
  [FIELDS_NAME.ENABLE_RANGE_SERVICES]: Boolean(
    data?.billingByServiceType ?? false,
  ),
  [FIELDS_NAME.SERVICES_TYPES]: parseSkillsStringToArray(
    data?.billingByServiceType?.serviceTypes ?? null,
  ),
});

const validateEnabledOptionString = (key: string) =>
  Yup.string().when(key, {
    is: true,
    then: (schema) => schema.required(VALIDATIONS_ERROR.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  });

const validateEnabledOptionArray = (key: string) =>
  Yup.array()
    .of(Yup.string())
    .when(key, {
      is: true,
      then: (schema) => schema.min(1, VALIDATIONS_ERROR.REQUIRED),
    });

export const validationSchema = Yup.object().shape({
  [FIELDS_NAME.CODE]: Yup.string().required(VALIDATIONS_ERROR.REQUIRED),
  [FIELDS_NAME.RATE]: Yup.string().required(VALIDATIONS_ERROR.REQUIRED),
  [FIELDS_NAME.ENABLE_RANGE_ZIP_CODES]: Yup.boolean(),
  [FIELDS_NAME.ZIP_CODES]: validateEnabledOptionString(
    FIELDS_NAME.ENABLE_RANGE_ZIP_CODES,
  ),
  [FIELDS_NAME.ENABLE_RANGE_UNIT_1]: Yup.boolean(),
  [FIELDS_NAME.UNIT_1_MIN]: validateEnabledOptionString(
    FIELDS_NAME.ENABLE_RANGE_UNIT_1,
  ),
  [FIELDS_NAME.UNIT_1_MAX]: validateEnabledOptionString(
    FIELDS_NAME.ENABLE_RANGE_UNIT_1,
  ),
  [FIELDS_NAME.ENABLE_RANGE_UNIT_2]: Yup.boolean(),
  [FIELDS_NAME.UNIT_2_MIN]: validateEnabledOptionString(
    FIELDS_NAME.ENABLE_RANGE_UNIT_2,
  ),
  [FIELDS_NAME.UNIT_2_MAX]: validateEnabledOptionString(
    FIELDS_NAME.ENABLE_RANGE_UNIT_2,
  ),
  [FIELDS_NAME.ENABLE_RANGE_PRICE]: Yup.boolean(),
  [FIELDS_NAME.PRICE_MIN]: validateEnabledOptionString(
    FIELDS_NAME.ENABLE_RANGE_PRICE,
  ),
  [FIELDS_NAME.PRICE_MAX]: validateEnabledOptionString(
    FIELDS_NAME.ENABLE_RANGE_PRICE,
  ),
  [FIELDS_NAME.ENABLE_RANGE_SERVICES]: Yup.boolean(),
  [FIELDS_NAME.SERVICES_TYPES]: validateEnabledOptionArray(
    FIELDS_NAME.ENABLE_RANGE_SERVICES,
  ),
});

export const getEnabledField = (
  formValues: ReturnType<typeof getInitialValues>,
  fieldName: string,
): boolean => get(formValues, fieldName, false);

export const getBaseBodyRequest = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  formValues: ReturnType<typeof getInitialValues>,
): Omit<AdminBillingListBillingUpadateBody, 'billingListId' | 'billingId'> => {
  let billZipCodeRange: Nullable<AdminBillingListBillingZipCodeRange> = null;

  if (getEnabledField(formValues, FIELDS_NAME.ENABLE_RANGE_ZIP_CODES)) {
    billZipCodeRange = {
      zipcodeRange: formValues[FIELDS_NAME.ZIP_CODES],
    };
  }

  let billingUnit1CapacityRange: Nullable<AdminBillingListBillingCapacityRange> =
    null;

  if (getEnabledField(formValues, FIELDS_NAME.ENABLE_RANGE_UNIT_1)) {
    billingUnit1CapacityRange = {
      unitMinimumCapacity: Number(formValues[FIELDS_NAME.UNIT_1_MIN]),
      unitMaximumCapacity: Number(formValues[FIELDS_NAME.UNIT_1_MAX]),
    };
  }

  let billingUnit2CapacityRange: Nullable<AdminBillingListBillingCapacityRange> =
    null;

  if (getEnabledField(formValues, FIELDS_NAME.ENABLE_RANGE_UNIT_2)) {
    billingUnit2CapacityRange = {
      unitMinimumCapacity: Number(formValues[FIELDS_NAME.UNIT_2_MIN]),
      unitMaximumCapacity: Number(formValues[FIELDS_NAME.UNIT_2_MAX]),
    };
  }

  let billingPriceRange: Nullable<AdminBillingListBillingPriceRange> = null;

  if (getEnabledField(formValues, FIELDS_NAME.ENABLE_RANGE_PRICE)) {
    billingPriceRange = {
      minimumPrice: Number(formValues[FIELDS_NAME.PRICE_MIN]),
      maximumPrice: Number(formValues[FIELDS_NAME.PRICE_MAX]),
    };
  }

  let billingByServiceType: Nullable<AdminBillingListBillingServiceTypes> =
    null;

  if (getEnabledField(formValues, FIELDS_NAME.ENABLE_RANGE_SERVICES)) {
    billingByServiceType = {
      serviceTypes: parseSkillListToString(
        formValues[FIELDS_NAME.SERVICES_TYPES],
      ),
    };
  }

  return {
    billingCode: formValues[FIELDS_NAME.CODE],
    shipPrice: Number(formValues[FIELDS_NAME.RATE]),
    billZipCodeRange,
    billingUnit1CapacityRange,
    billingUnit2CapacityRange,
    billingPriceRange,
    billingByServiceType,
  };
};
